import { NextUrqlContext, SSRExchange } from 'next-urql';
import env from 'app/env';
import { isSearchQuery } from 'app/utils/searchUtils';

import {
    dedupExchange,
    fetchExchange,
    cacheExchange,
    errorExchange,
    ClientOptions,
    CombinedError,
    Operation,
} from 'urql';

import { persistedFetchExchange } from '@urql/exchange-persisted-fetch';
import { requestPolicyExchange } from '@urql/exchange-request-policy';
import { Debug } from '@svt/debug';
import * as Sentry from '@sentry/nextjs';
import getConfig from 'next/config';

const { serverRuntimeConfig } = getConfig();

const { error: errorLogger } = Debug('play:urql:error');

let url = env.CONTENTO_API;
let searchUrl = env.CONTENTO_SEARCH_API;

/* 
    For SSR and in production, we want to use an .svc-url that points 
    directly to Contento (within the same Kubernetes cluster).
*/
if (serverRuntimeConfig?.CONTENTO_API_SSR) {
    url = `${serverRuntimeConfig.CONTENTO_API_SSR}`;
}

if (serverRuntimeConfig?.CONTENTO_SEARCH_API_SSR) {
    searchUrl = `${serverRuntimeConfig.CONTENTO_SEARCH_API_SSR}`;
}

const fetchOptions = {
    headers: {
        'User-Agent': env.USER_AGENT,
    },
};

const extraSettings = typeof window === 'undefined' ? fetchOptions : {};

const urqlFetch = (input: RequestInfo, init?: RequestInit) => {
    let newInit = init;
    let newInput = input;
    // Fetch fails in iOS 10.??/Safari 10 if headers is undefined
    // https://bugs.webkit.org/show_bug.cgi?id=169970
    // https://bugs.webkit.org/show_bug.cgi?id=168043
    if (init && init.hasOwnProperty('headers') && typeof init.headers === 'undefined') {
        const { headers, ...allExceptHeaders } = init; // eslint-disable-line
        newInit = allExceptHeaders;
    }

    if (typeof newInput === 'string') {
        if (isSearchQuery(newInput, init)) {
            newInput = newInput.replace(url, searchUrl);
        }

        if (newInput.includes('?')) {
            newInput += `&ua=${env.USER_AGENT}-client`;
        } else {
            newInput += `?ua=${env.USER_AGENT}-client`;
        }
    }

    return fetch(newInput, newInit);
};

const handleError = (error: CombinedError, operation: Operation) => {
    const queryName = (operation as any).query.definitions[0].name?.value || 'Unknown';

    let networkError;
    if (error.networkError) {
        networkError = error.networkError.message;
    }

    errorLogger(
        'Query %s returned error : graphQLErrors=%s networkError=%s message=%s',
        queryName,
        error.graphQLErrors.join(', '),
        networkError,
        error.message
    );

    if (typeof window !== 'undefined') {
        Sentry.captureException(error);
    }
};

const config: ClientOptions = {
    url,
    fetchOptions: extraSettings,
    fetch: urqlFetch,
    preferGetMethod: true,
    exchanges: [
        dedupExchange,
        errorExchange({
            onError: handleError,
        }),
        cacheExchange,
        persistedFetchExchange({}),
        fetchExchange,
    ],
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const withCtxFetch = (ctx: NextUrqlContext) => (input: RequestInfo, init?: RequestInit) =>
    urqlFetch(input, init);

export const withUrqlSsr = (ssrExchange: SSRExchange, ctx?: NextUrqlContext): ClientOptions => ({
    ...config,
    fetch: withCtxFetch(ctx),
    exchanges: [
        dedupExchange,
        errorExchange({
            onError: handleError,
        }),
        requestPolicyExchange({
            ttl: 30 * 1000,
        }),
        cacheExchange,
        ssrExchange, // order matters
        persistedFetchExchange(),
        fetchExchange,
    ],
});

export const urqlConfig = config;
